import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Toolbar,
  alpha,
  Skeleton,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

const STATUS_OPTIONS = [
  { label: 'Profile Added',       color: '#e0f2fe', text: '#0369a1' },
  { label: 'Applied',             color: '#f0fdf4', text: '#15803d' },
  { label: 'Under Review',        color: '#fefce8', text: '#a16207' },
  { label: 'Shortlisted',         color: '#f0fdf4', text: '#166534' },
  { label: 'Interview Scheduled', color: '#eff6ff', text: '#1d4ed8' },
  { label: 'Interview Completed', color: '#eef2ff', text: '#4338ca' },
  { label: 'Offer Extended',      color: '#fdf4ff', text: '#7e22ce' },
  { label: 'Offer Accepted',      color: '#f0fdf4', text: '#14532d' },
  { label: 'Offer Declined',      color: '#fff7ed', text: '#c2410c' },
  { label: 'Rejected',            color: '#fef2f2', text: '#b91c1c' },
  { label: 'On Hold',             color: '#f8fafc', text: '#475569' },
  { label: 'Hired',               color: '#f0fdf4', text: '#065f46' },
  { label: 'Withdrawn',           color: '#fafafa', text: '#6b7280' },
];

function DataTable({
  rows,
  columns,
  data,
  loading,
  onSearch,
  onClearSearch,
  onExport,
  onDeleteSelected,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageProp,
  searchValue,
  currentPage,
  statusFilter,
  onStatusChange,
}) {
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState(searchValue || '');
  const page = currentPage;

  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageProp || 10);

  useEffect(() => {
    if (typeof rowsPerPageProp === 'number' && rowsPerPageProp !== rowsPerPage) {
      setRowsPerPage(rowsPerPageProp);
    }
  }, [rowsPerPageProp, rowsPerPage]);

  useEffect(() => {
    if (typeof searchValue === 'string' && searchValue !== searchQuery) {
      setSearchQuery(searchValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const handleSearchSubmit = useCallback(
    (e) => {
      e.preventDefault();
      onSearch(searchQuery);
    },
    [onSearch, searchQuery]
  );

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    onClearSearch();
  }, [onClearSearch]);

  const handleSelectAllClick = useCallback(
    (event) => {
      if (event.target.checked) {
        const newSelected = rows.map((n) => n.id);
        setSelectedRows(newSelected);
      } else {
        setSelectedRows([]);
      }
    },
    [rows]
  );

  const handleRowClick = useCallback((id) => {
    setSelectedRows((prev) => {
      const selectedIndex = prev.indexOf(id);
      let newSelected = [];

      if (selectedIndex === -1) newSelected = [...prev, id];
      else if (selectedIndex === 0) newSelected = prev.slice(1);
      else if (selectedIndex === prev.length - 1) newSelected = prev.slice(0, -1);
      else newSelected = [...prev.slice(0, selectedIndex), ...prev.slice(selectedIndex + 1)];

      return newSelected;
    });
  }, []);

  const handleChangePage = useCallback(
    (event, newPage) => {
      const hasNext = data.pagination?.has_next;
      const hasPrev = data.pagination?.has_prev;

      if ((newPage > page && !hasNext) || (newPage < page && !hasPrev)) return;
      onPageChange(newPage);
    },
    [data.pagination, onPageChange, page]
  );

  const handleChangeRowsPerPage = useCallback(
    (event) => {
      const value = parseInt(event.target.value, 10);
      setRowsPerPage(value);
      onRowsPerPageChange(value);
    },
    [onRowsPerPageChange]
  );

  const isSelected = useCallback((id) => selectedRows.indexOf(id) !== -1, [selectedRows]);
  const numSelected = selectedRows.length;
  const rowCount = rows.length;

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        overflow: 'hidden',
        borderRadius: 3,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}
    >
      {/* Toolbar */}
      <Toolbar
        sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          justifyContent: 'space-between',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#fafafa',
          ...(numSelected > 0 && {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.15),
          }),
        }}
      >
        <form onSubmit={handleSearchSubmit} style={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
          <TextField
            placeholder="Search candidates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            variant="outlined"
            sx={{ flex: 1, maxWidth: 350 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment:
                searchQuery && (
                  <InputAdornment position="end" onClick={handleClearSearch} style={{ cursor: 'pointer' }}>
                    <ClearIcon />
                  </InputAdornment>
                ),
            }}
          />
          <Button type="submit" variant="contained" sx={{ ml: 1, height: '36px', textTransform: 'none' }}>
            Search
          </Button>
        </form>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Status filter dropdown */}
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <Select
              displayEmpty
              value={statusFilter || ''}
              onChange={(e) => onStatusChange && onStatusChange(e.target.value)}
              disabled={loading}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#cbd5e1', flexShrink: 0 }} />
                      <span style={{ fontSize: 13, color: '#94a3b8' }}>All statuses</span>
                    </Box>
                  );
                }
                const match = STATUS_OPTIONS.find((s) => s.label === selected);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: match?.text || '#64748b', flexShrink: 0 }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: match?.text || '#1e293b' }}>{selected}</span>
                  </Box>
                );
              }}
              sx={{
                fontSize: 13,
                backgroundColor: '#ffffff',
                borderRadius: 2,
                height: 36,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#93c5fd' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6', borderWidth: 1.5 },
              }}
            >
              <MenuItem value="">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#cbd5e1' }} />
                  <span style={{ fontSize: 13, color: '#475569' }}>All statuses</span>
                </Box>
              </MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.label} value={s.label}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: s.text }} />
                    <span style={{ fontSize: 13, color: '#1e293b' }}>{s.label}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            onClick={onExport}
            disabled={loading}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 13,
              borderColor: '#6366f1',
              color: '#6366f1',
              borderRadius: 2,
              py: 0.75,
            }}
          >
            Export
          </Button>

          <Button
            onClick={() => onDeleteSelected(selectedRows)}
            disabled={loading || numSelected === 0}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 13,
              backgroundColor: '#ef4444',
              borderRadius: 2,
              py: 0.75,
              '&:hover': { backgroundColor: '#dc2626' },
              '&:disabled': { backgroundColor: '#f3f4f6', color: '#9ca3af' },
            }}
            variant="contained"
          >
            Delete
          </Button>
        </Box>
      </Toolbar>

      {/* Table */}
      <TableContainer
        sx={{
          width: '100%',
          maxHeight: '70vh',
          overflow: 'auto',
          borderRadius: 2,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { height: 8, width: 8 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#cbd5e1',
            borderRadius: 8,
          },
        }}
      >
        <Table
          stickyHeader
          sx={{
            width: '100%',
            tableLayout: 'auto',
            borderCollapse: 'separate',
            borderSpacing: 0,
            '& th': {
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#f9fafb',
              fontWeight: 700,
              fontSize: 13,
              color: '#374151',
              whiteSpace: 'nowrap',
              borderBottom: '1px solid #e5e7eb',
              textTransform: 'capitalize',
            },
            '& td': {
              fontSize: 13,
              color: '#111827',
              borderBottom: '1px solid #f3f4f6',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                  checked={rowCount > 0 && numSelected === rowCount}
                  onChange={handleSelectAllClick}
                  disabled={loading}
                />
              </TableCell>
              {columns.map((column) => (
                <TableCell key={column.field}>{column.headerName}</TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              [...Array(rowsPerPage)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell padding="checkbox">
    <Skeleton
        variant="rounded"
        width={18}
        height={18}
        animation="wave"
        sx={{ borderRadius: 0.5 }}
      />
    </TableCell>
                  {columns.map((column) => (
                    <TableCell key={column.field}>
                   <Skeleton
          variant="rounded"
          height={20}
          animation="wave"
          sx={{ borderRadius: 1 }}
        />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 3, color: '#6b7280' }}>
                  No candidates found
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => {
                const isItemSelected = isSelected(row.id);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    selected={isItemSelected}
                    onClick={() => handleRowClick(row.id)}
                    sx={{
                      transition: 'background-color 0.2s ease',
                      '&:hover': { backgroundColor: '#f3f4f6' },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox color="primary" checked={isItemSelected} />
                    </TableCell>
                    {columns.map((column) => {
                      const rawValue = row[column.field];
                      const safePrimitive =
                        rawValue === undefined || rawValue === null || rawValue === '' ? 'N/A' : rawValue;
                      let cellContent;

                      if (column.renderCell) {
                        cellContent = column.renderCell({ ...row, [column.field]: safePrimitive }, index);
                        if (cellContent === undefined || cellContent === null || cellContent === '') {
                          cellContent = (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                height: '100%',
                              }}
                            >
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 0.25,
                                  borderRadius: '8px',
                                  backgroundColor: '#f3f4f6',
                                  color: '#9ca3af',
                                  fontStyle: 'italic',
                                  fontSize: 12,
                                  fontWeight: 500,
                                  minWidth: 24,
                                  textAlign: 'center',
                                  border: '1px solid #e5e7eb',
                                }}
                              >
                                --
                              </Box>
                            </Box>
                          );
                        }
                      } else {
                        cellContent = safePrimitive;
                      }

                      return <TableCell key={column.field}>{cellContent}</TableCell>;
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={data.pagination?.total_records || 0}
        page={page}
        onPageChange={(event, newPage) => {
          if (!loading) handleChangePage(event, newPage);
        }}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          if (!loading) handleChangeRowsPerPage(event);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        nextIconButtonProps={{
          disabled: loading || data.pagination?.has_next === false,
        }}
        backIconButtonProps={{
          disabled: loading || data.pagination?.has_prev === false,
        }}
        SelectProps={{ disabled: loading }}
        labelRowsPerPage="Rows per page"
        sx={{
          borderTop: '1px solid #e5e7eb',
          backgroundColor: loading ? '#f3f4f6' : '#fafafa',
          opacity: loading ? 0.6 : 1,
          pointerEvents: loading ? 'none' : 'auto',
          transition: 'opacity 0.3s ease',
          fontSize: 13,
          '& .MuiTablePagination-toolbar': { minHeight: '48px' },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: 13,
          },
        }}
      />
    </Paper>
  );
}

export default React.memo(DataTable);